let audioContextPromise: Promise<AudioContext> | null = null;
const activeSources = new Set<AudioBufferSourceNode>();

async function getAudioContext(): Promise<AudioContext> {
  if (!audioContextPromise) {
    audioContextPromise = Promise.resolve(
      new AudioContext({ latencyHint: "interactive" }),
    );
  }

  return audioContextPromise;
}

export async function playFeedbackTone(frequency: number): Promise<void> {
  const context = await getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.connect(gain);
  gain.connect(context.destination);

  oscillator.type = "sine";
  oscillator.frequency.value = frequency;
  gain.gain.value = 0.02;

  oscillator.start();
  oscillator.stop(context.currentTime + 0.08);
}

export function stopPlayback(): void {
  activeSources.forEach((source) => {
    try {
      source.stop();
    } catch {
      // Playback may already have ended.
    }
  });
  activeSources.clear();
}

export async function playPcmChannels(
  channels: Float32Array[],
  sampleRate: number,
  options: {
    signal?: AbortSignal;
  } = {},
): Promise<void> {
  if (!channels.length || !channels[0]?.length) {
    return;
  }

  const context = await getAudioContext();

  if (context.state === "suspended") {
    await context.resume();
  }

  const frameCount = channels[0].length;
  const channelCount = channels.length;
  const buffer = context.createBuffer(channelCount, frameCount, sampleRate);

  for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
    const data = buffer.getChannelData(channelIndex);
    data.set(channels[channelIndex]);
  }

  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);

  await new Promise<void>((resolve, reject) => {
    const handleAbort = () => {
      try {
        source.stop();
      } catch {
        // Ignore repeated stop attempts.
      }
      activeSources.delete(source);
      reject(new DOMException("Playback interrupted", "AbortError"));
    };

    activeSources.add(source);
    source.addEventListener(
      "ended",
      () => {
        activeSources.delete(source);
        options.signal?.removeEventListener("abort", handleAbort);
        resolve();
      },
      { once: true },
    );

    if (options.signal?.aborted) {
      handleAbort();
      return;
    }

    options.signal?.addEventListener("abort", handleAbort, { once: true });
    source.start();
  });
}
